import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import {
  UploadCloud, Download, CheckCircle2, AlertCircle, Loader2,
  RefreshCw, FileSpreadsheet, Info, Filter, History, Calendar,
  User as UserIcon, X, ArrowRight, Eye, ShieldAlert, XCircle, Slash
} from 'lucide-react';

interface RowLogItem {
  id?: string;
  rowNum: number;
  ticketId: string;
  ideaName: string;
  uploadedStatus?: string;
  userEmail?: string;
  reason: string;
  previousStatus?: string;
  newStatus?: string;
  actionType?: string;
  status: 'SUCCESS' | 'FAILED' | 'SKIPPED';
  errorReason?: string;
}

interface ImportSummaryData {
  importLogId?: string;
  uploadType: 'REJECT' | 'DUPLICATE';
  totalCount: number;
  successCount: number;
  failedCount: number;
  mismatchedCount?: number;
  skippedCount: number;
  rowLogs: RowLogItem[];
}

interface ImportLogSession {
  id: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  importType?: string;
  createdAt: string;
  importedBy: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

export function BulkStatusImportPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'NEW_IMPORT' | 'HISTORY'>('NEW_IMPORT');
  const [uploadOption, setUploadOption] = useState<'AUTO' | 'REJECT' | 'DUPLICATE'>('AUTO');

  // File Upload States
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [importResult, setImportResult] = useState<ImportSummaryData | null>(null);
  const [logFilter, setLogFilter] = useState<'ALL' | 'SUCCESS' | 'FAILED' | 'SKIPPED'>('ALL');

  // History Details Modal State
  const [selectedHistoryLogId, setSelectedHistoryLogId] = useState<string | null>(null);

  // Query past import logs list
  const { data: historyLogsData, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['bulkImportLogsList'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/ideas/bulk-import-logs');
      return res.data.data as ImportLogSession[];
    },
  });

  const statusHistoryLogs = (historyLogsData || []).filter(
    (l) => l.importType === 'STATUS_UPDATE_REJECT' || l.importType === 'STATUS_UPDATE_DUPLICATE' || l.importType === 'STATUS_UPDATE_AUTO'
  );

  // Query details for selected history log modal
  const { data: historyLogDetailsData, isLoading: detailsLoading } = useQuery({
    queryKey: ['bulkImportLogDetails', selectedHistoryLogId],
    queryFn: async () => {
      if (!selectedHistoryLogId) return null;
      const res = await apiClient.get(`/admin/ideas/bulk-import-logs/${selectedHistoryLogId}`);
      return res.data.data;
    },
    enabled: !!selectedHistoryLogId,
  });

  const handleDownloadSample = () => {
    let sampleData: any[] = [];
    if (uploadOption === 'REJECT') {
      sampleData = [
        {
          'Ticket ID': 'IDE-1001',
          'Idea Name': 'Reduce Paper Usage',
          'Status': 'Reject',
          'User Email': 'user@company.com',
          'Reason': 'Not feasible due to current process constraints',
        },
        {
          'Ticket ID': 'IDE-1002',
          'Idea Name': 'Legacy Printing Process Update',
          'Status': 'Reject',
          'User Email': 'evaluator@company.com',
          'Reason': 'Superseded by digital transformation initiative',
        },
      ];
    } else if (uploadOption === 'DUPLICATE') {
      sampleData = [
        {
          'Ticket ID': 'IDE-1002',
          'Idea Name': 'Digital Approval Flow',
          'Status': 'Duplicate',
          'User Email': 'user2@company.com',
          'Reason': 'Already submitted earlier under ticket IDE-0980',
        },
        {
          'Ticket ID': 'IDE-1005',
          'Idea Name': 'Automated Invoice Review',
          'Status': 'Duplicate',
          'User Email': 'reviewer@company.com',
          'Reason': 'Identical proposal registered in previous quarter',
        },
      ];
    } else {
      // Auto-Detect / Mixed Sample
      sampleData = [
        {
          'Ticket ID': 'IDE-1001',
          'Idea Name': 'Reduce Paper Usage',
          'Status': 'Reject',
          'User Email': 'user@company.com',
          'Reason': 'Not feasible due to current process constraints',
        },
        {
          'Ticket ID': 'IDE-1002',
          'Idea Name': 'Digital Approval Flow',
          'Status': 'Duplicate',
          'User Email': 'user2@company.com',
          'Reason': 'Already submitted earlier under ticket IDE-0980',
        },
      ];
    }

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `Bulk_${uploadOption}_Sample`);
    XLSX.writeFile(workbook, `Bulk_Idea_Status_Update_${uploadOption}_Sample.xlsx`);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setErrorMessage('');
    }
  };

  const handleUploadAndProcess = async () => {
    if (!file) {
      setErrorMessage('Please select an Excel file (.xlsx, .xls, or .csv) to import.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];

      const rawRows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rawRows.length === 0) {
        setErrorMessage('The selected Excel file contains no data rows.');
        setIsProcessing(false);
        return;
      }

      // Map Excel column names dynamically
      const parsedRows = rawRows.map((r: any, idx: number) => {
        const findVal = (possibleKeys: string[]) => {
          for (const key of Object.keys(r)) {
            const cleanKey = key.trim().toLowerCase();
            if (possibleKeys.some((p) => cleanKey.includes(p.toLowerCase()))) {
              return r[key];
            }
          }
          return '';
        };

        const ticketId = findVal(['ticket id', 'ticketid', 'ticket_id', 'ticket']);
        const ideaName = findVal(['idea name', 'ideaname', 'idea_name', 'name of idea', 'title', 'name']);
        const status = findVal(['status', 'uploaded status', 'action', 'update status']);
        const userEmail = findVal(['user email', 'useremail', 'user_email', 'email', 'performed by']);
        const reason = findVal(['reason', 'remarks', 'comments', 'comment', 'note', 'rejection reason']);

        return {
          rowNum: idx + 2,
          ticketId: String(ticketId).trim(),
          ideaName: String(ideaName).trim(),
          status: String(status).trim(),
          userEmail: String(userEmail).trim(),
          reason: String(reason).trim(),
        };
      });

      const res = await apiClient.post('/admin/ideas/bulk-status-import', {
        uploadType: uploadOption,
        rows: parsedRows,
      });

      if (res.data.success) {
        setImportResult(res.data.data);
        queryClient.invalidateQueries({ queryKey: ['leaderIdeas'] });
        queryClient.invalidateQueries({ queryKey: ['adminIdeas'] });
        queryClient.invalidateQueries({ queryKey: ['adminStats'] });
        queryClient.invalidateQueries({ queryKey: ['bulkImportLogsList'] });
      } else {
        setErrorMessage(res.data.error?.message || 'Failed to process import.');
      }
    } catch (err: any) {
      console.error('Import Error:', err);
      setErrorMessage(err.response?.data?.error?.message || err.message || 'Error processing Excel file.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadErrorReport = () => {
    if (!importResult || !importResult.rowLogs) return;

    const reportData = importResult.rowLogs.map((log) => ({
      'Row #': log.rowNum,
      'Ticket ID': log.ticketId,
      'Idea Name': log.ideaName,
      'Status': log.uploadedStatus || (uploadOption === 'REJECT' ? 'Reject' : 'Duplicate'),
      'User Email': log.userEmail || '',
      'Reason': log.reason || '',
      'Result': log.status,
      'Error Reason': log.errorReason || '-',
    }));

    const worksheet = XLSX.utils.json_to_sheet(reportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Import Result & Errors');
    XLSX.writeFile(
      workbook,
      `Bulk_${importResult.uploadType}_Status_Import_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleReset = () => {
    setFile(null);
    setImportResult(null);
    setErrorMessage('');
    setLogFilter('ALL');
  };

  const filteredLogs =
    importResult?.rowLogs.filter((log) => {
      if (logFilter === 'SUCCESS') return log.status === 'SUCCESS';
      if (logFilter === 'FAILED') return log.status === 'FAILED';
      if (logFilter === 'SKIPPED') return log.status === 'SKIPPED';
      return true;
    }) || [];

  return (
    <Layout title="Bulk Idea Status Update Import">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-red-600" />
            Bulk Idea Status Update Import
          </h2>
          <p className="text-slate-500 text-xs mt-1">
            Bulk update Ideas as Reject or Duplicate with strict primary validation and comprehensive audit logging
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownloadSample}
          className="px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-xl shadow-sm transition-all flex items-center space-x-2 self-start sm:self-auto cursor-pointer"
        >
          <Download className="w-4 h-4 text-indigo-600" />
          <span>Download Sample Excel ({uploadOption})</span>
        </button>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex border-b border-slate-200 mb-6 bg-slate-50/50 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('NEW_IMPORT')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeTab === 'NEW_IMPORT'
            ? 'bg-white text-indigo-700 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
        >
          <UploadCloud className="w-4 h-4" />
          <span>New Status Update Import</span>
        </button>
        <button
          onClick={() => {
            setActiveTab('HISTORY');
            refetchHistory();
          }}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeTab === 'HISTORY'
            ? 'bg-white text-indigo-700 shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
        >
          <History className="w-4 h-4" />
          <span>Import History Logs ({statusHistoryLogs.length})</span>
        </button>
      </div>

      {/* NEW IMPORT TAB CONTENT */}
      {activeTab === 'NEW_IMPORT' && (
        <div className="space-y-6">
          {!importResult ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              {/* Step 1: Upload Option Selection */}
              <div className="mb-6">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Select Upload Option <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
                  <button
                    type="button"
                    onClick={() => setUploadOption('AUTO')}
                    className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer flex items-start space-x-3 ${uploadOption === 'AUTO'
                      ? 'border-indigo-500 bg-indigo-50/40 text-indigo-900 shadow-sm'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                      }`}
                  >
                    <RefreshCw className={`w-5 h-5 mt-0.5 ${uploadOption === 'AUTO' ? 'text-indigo-600' : 'text-slate-400'}`} />
                    <div>
                      <div className="font-bold text-sm">Auto-Detect / Mixed File</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Supports single Excel file containing both Reject & Duplicate status rows
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setUploadOption('REJECT')}
                    className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer flex items-start space-x-3 ${uploadOption === 'REJECT'
                      ? 'border-red-500 bg-red-50/40 text-red-900 shadow-sm'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                      }`}
                  >
                    <XCircle className={`w-5 h-5 mt-0.5 ${uploadOption === 'REJECT' ? 'text-red-600' : 'text-slate-400'}`} />
                    <div>
                      <div className="font-bold text-sm">Reject Only</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Bulk reject ideas with mandatory reason & Evaluate Idea validation check
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setUploadOption('DUPLICATE')}
                    className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer flex items-start space-x-3 ${uploadOption === 'DUPLICATE'
                      ? 'border-amber-500 bg-amber-50/40 text-amber-900 shadow-sm'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                      }`}
                  >
                    <Slash className={`w-5 h-5 mt-0.5 ${uploadOption === 'DUPLICATE' ? 'text-amber-600' : 'text-slate-400'}`} />
                    <div>
                      <div className="font-bold text-sm">Duplicate Only</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Bulk mark ideas as duplicate with mandatory reason & audit tracking
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Guidelines Box */}
              <div className="bg-blue-50/60 border border-blue-200 rounded-xl p-4 mb-6">
                <h4 className="text-xs font-bold text-blue-900 flex items-center gap-1.5 mb-2">
                  <Info className="w-4 h-4 text-blue-600" />
                  <span>Important Bulk Status Update Rules & Guidelines:</span>
                </h4>
                <ul className="text-xs text-blue-800 space-y-1.5 list-disc pl-5">
                  <li>
                    <strong>Matching Requirement:</strong> Both <code>Ticket ID</code> and <code>Idea Name</code> are required and must strictly match system records. Mismatched rows will be marked as Failed.
                  </li>
                  <li>
                    <strong>Status Column:</strong> Must contain <code>{uploadOption === 'REJECT' ? 'Reject' : 'Duplicate'}</code> matching the selected upload option.
                  </li>
                  <li>
                    <strong>User Email:</strong> Must contain a valid registered email of a user/committee member belonging to the target idea's committee/department scope (if not mapped to that committee, the row will be failed/skipped).
                  </li>
                  <li>
                    <strong>Mandatory Reason:</strong> The <code>Reason</code> field is mandatory and must not be empty or whitespace-only.
                  </li>
                  {uploadOption === 'REJECT' ? (
                    <li>
                      <strong>Evaluate Idea Restriction:</strong> Ideas already in <em>Evaluate Idea</em> status or evaluated/validated will be automatically skipped without re-rejecting.
                    </li>
                  ) : (
                    <li>
                      <strong>Duplicate Update:</strong> Matching valid ideas will be updated to <em>Duplicate</em> status along with timeline audit logs.
                    </li>
                  )}
                  <li>
                    <strong>Partial Processing:</strong> Valid rows will be processed successfully even if some rows fail validation.
                  </li>
                </ul>
              </div>

              {/* Step 2: Excel / CSV File Dropzone */}
              <div className="mb-6">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Upload Excel File (.xlsx, .xls, .csv) <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-slate-300 hover:border-indigo-400 rounded-2xl p-8 text-center bg-slate-50/50 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="bulk-status-file-input"
                  />
                  <label htmlFor="bulk-status-file-input" className="cursor-pointer flex flex-col items-center">
                    <div className="p-3 bg-white rounded-full shadow-sm border border-slate-200 mb-3">
                      <UploadCloud className="w-8 h-8 text-indigo-600" />
                    </div>
                    <span className="text-sm font-bold text-slate-700">
                      {file ? file.name : 'Click to browse or drag & drop Excel file'}
                    </span>
                    <span className="text-xs text-slate-400 mt-1">Supports .XLSX, .XLS, or .CSV file format</span>
                  </label>
                </div>
              </div>

              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleUploadAndProcess}
                  disabled={!file || isProcessing}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center space-x-2 disabled:opacity-50 cursor-pointer"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Processing Updates...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Upload & Process Updates</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* IMPORT RESULT & SUMMARY REPORT VIEW */
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                  <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Records</div>
                  <div className="text-2xl font-black text-slate-800 mt-1">{importResult.totalCount}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl shadow-sm">
                  <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Successfully Updated</div>
                  <div className="text-2xl font-black text-emerald-700 mt-1">{importResult.successCount}</div>
                </div>
                <div className="bg-red-50 border border-red-200 p-4 rounded-xl shadow-sm">
                  <div className="text-[11px] font-bold text-red-700 uppercase tracking-wider">Failed Records</div>
                  <div className="text-2xl font-black text-red-700 mt-1">{importResult.failedCount}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl shadow-sm">
                  <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">Mismatched</div>
                  <div className="text-2xl font-black text-amber-700 mt-1">{importResult.mismatchedCount ?? 0}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl shadow-sm">
                  <div className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Skipped Records</div>
                  <div className="text-2xl font-black text-slate-600 mt-1">{importResult.skippedCount}</div>
                </div>
              </div>

              {/* Action Toolbar */}
              <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-bold text-slate-700">Filter Log Rows:</span>
                  <div className="flex bg-slate-100 p-1 rounded-lg">
                    {(['ALL', 'SUCCESS', 'FAILED', 'SKIPPED'] as const).map((filterOpt) => (
                      <button
                        key={filterOpt}
                        onClick={() => setLogFilter(filterOpt)}
                        className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${logFilter === filterOpt ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                          }`}
                      >
                        {filterOpt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleDownloadErrorReport}
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center space-x-2 cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Error Report</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl shadow-sm transition-all flex items-center space-x-2 cursor-pointer"
                  >
                    <RefreshCw className="w-4 h-4 text-slate-500" />
                    <span>Import Another File</span>
                  </button>
                </div>
              </div>

              {/* Row Logs Table */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-900 text-white font-bold uppercase tracking-wider">
                      <tr>
                        <th className="p-3.5 w-16 text-center">Row #</th>
                        <th className="p-3.5">Ticket ID</th>
                        <th className="p-3.5">Idea Name</th>
                        <th className="p-3.5">Uploaded Status</th>
                        <th className="p-3.5">User Email</th>
                        <th className="p-3.5">Reason</th>
                        <th className="p-3.5 w-28 text-center">Result</th>
                        <th className="p-3.5">Error Reason / Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                      {filteredLogs.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-slate-400">
                            No log rows match the selected filter.
                          </td>
                        </tr>
                      ) : (
                        filteredLogs.map((log, idx) => (
                          <tr
                            key={idx}
                            className={`hover:bg-slate-50 ${log.status === 'FAILED'
                              ? 'bg-red-50/20'
                              : log.status === 'SKIPPED'
                                ? 'bg-amber-50/20'
                                : 'bg-white'
                              }`}
                          >
                            <td className="p-3.5 text-center font-mono text-slate-500">{log.rowNum}</td>
                            <td className="p-3.5 font-bold text-indigo-700">{log.ticketId}</td>
                            <td className="p-3.5 font-semibold text-slate-800 max-w-xs truncate">{log.ideaName}</td>
                            <td className="p-3.5">{log.uploadedStatus || '-'}</td>
                            <td className="p-3.5 font-mono text-[11px] text-slate-600">{log.userEmail || '-'}</td>
                            <td className="p-3.5 max-w-xs text-slate-600 truncate">{log.reason || '-'}</td>
                            <td className="p-3.5 text-center">
                              <span
                                className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${log.status === 'SUCCESS'
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                  : log.status === 'FAILED'
                                    ? 'bg-red-100 text-red-800 border border-red-200'
                                    : 'bg-slate-200 text-slate-800 border border-slate-300'
                                  }`}
                              >
                                {log.status}
                              </span>
                            </td>
                            <td
                              className={`p-3.5 ${log.status === 'FAILED'
                                ? 'text-red-700 font-semibold'
                                : log.status === 'SKIPPED'
                                  ? 'text-amber-700 font-semibold'
                                  : 'text-slate-500'
                                }`}
                            >
                              {log.errorReason || log.reason || '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* HISTORY TAB CONTENT */}
      {activeTab === 'HISTORY' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <History className="w-4 h-4 text-indigo-600" />
              <span>Past Status Update Upload History</span>
            </h3>
            <button
              onClick={() => refetchHistory()}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 cursor-pointer"
              title="Refresh History"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {historyLoading ? (
            <div className="py-12 text-center text-slate-400 flex flex-col items-center space-y-2">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              <span className="text-xs">Loading import history logs...</span>
            </div>
          ) : statusHistoryLogs.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-xs">
              No status update import history found.
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-700 font-bold uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="p-3.5">Import Date & Time</th>
                    <th className="p-3.5">Imported By</th>
                    <th className="p-3.5 text-center">Type</th>
                    <th className="p-3.5 text-center">Total</th>
                    <th className="p-3.5 text-center">Success</th>
                    <th className="p-3.5 text-center">Failed</th>
                    <th className="p-3.5 text-center">Skipped</th>
                    <th className="p-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                  {statusHistoryLogs.map((logSession) => (
                    <tr key={logSession.id} className="hover:bg-slate-50">
                      <td className="p-3.5 font-bold text-slate-800">
                        {new Date(logSession.createdAt).toLocaleString()}
                      </td>
                      <td className="p-3.5">
                        <div className="font-semibold text-slate-900">{logSession.importedBy?.name || 'N/A'}</div>
                        <div className="text-[10px] text-slate-400">{logSession.importedBy?.email}</div>
                      </td>
                      <td className="p-3.5 text-center">
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 font-bold rounded text-[10px]">
                          {logSession.importType || 'STATUS_UPDATE'}
                        </span>
                      </td>
                      <td className="p-3.5 text-center font-bold text-slate-800">{logSession.totalCount}</td>
                      <td className="p-3.5 text-center font-bold text-emerald-600">{logSession.successCount}</td>
                      <td className="p-3.5 text-center font-bold text-red-600">{logSession.failedCount}</td>
                      <td className="p-3.5 text-center font-bold text-slate-500">{logSession.skippedCount}</td>
                      <td className="p-3.5 text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedHistoryLogId(logSession.id)}
                          className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-indigo-600 font-bold rounded-lg text-xs transition-colors flex items-center space-x-1 ml-auto cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>View Details</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* HISTORY LOG DETAILS MODAL */}
      {selectedHistoryLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 shadow-2xl space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                <span>Import Session Details Log</span>
              </h3>
              <button
                onClick={() => setSelectedHistoryLogId(null)}
                className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailsLoading ? (
              <div className="py-16 text-center text-slate-400 flex flex-col items-center space-y-2">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                <span className="text-xs">Fetching import row details...</span>
              </div>
            ) : historyLogDetailsData ? (
              <div className="space-y-4 overflow-y-auto pr-1 flex-1">
                {/* Header Metadata */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3 rounded-xl text-xs">
                  <div>
                    <span className="text-slate-400 text-[10px] uppercase font-bold block">Imported By</span>
                    <span className="font-bold text-slate-800">{historyLogDetailsData.importedBy?.name}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] uppercase font-bold block">Date & Time</span>
                    <span className="font-semibold text-slate-700">
                      {new Date(historyLogDetailsData.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] uppercase font-bold block">Total Rows</span>
                    <span className="font-bold text-slate-800">{historyLogDetailsData.totalCount}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-[10px] uppercase font-bold block">Status Breakdown</span>
                    <span className="font-semibold text-emerald-600">
                      {historyLogDetailsData.successCount} Success
                    </span>{' '}
                    / <span className="font-semibold text-red-600">{historyLogDetailsData.failedCount} Failed</span>
                  </div>
                </div>

                {/* Rows Table */}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-900 text-white font-bold uppercase tracking-wider">
                      <tr>
                        <th className="p-3 w-14 text-center">Row #</th>
                        <th className="p-3">Ticket ID</th>
                        <th className="p-3">Idea Name</th>
                        <th className="p-3">User Email</th>
                        <th className="p-3">Reason</th>
                        <th className="p-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                      {historyLogDetailsData.rows?.map((row: any, idx: number) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-3 text-center font-mono text-slate-500">{row.rowNum}</td>
                          <td className="p-3 font-bold text-indigo-700">{row.ticketId}</td>
                          <td className="p-3 font-semibold text-slate-800 max-w-xs truncate">{row.ideaName}</td>
                          <td className="p-3 font-mono text-[11px] text-slate-600">{row.userEmail || '-'}</td>
                          <td className="p-3 max-w-xs text-slate-600 truncate">{row.reason || '-'}</td>
                          <td className="p-3 text-center">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${row.status === 'SUCCESS'
                                ? 'bg-emerald-100 text-emerald-800'
                                : row.status === 'FAILED'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-slate-200 text-slate-800'
                                }`}
                            >
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="border-t border-slate-200 pt-3 flex justify-end">
              <button
                onClick={() => setSelectedHistoryLogId(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
